import { type Ref } from 'vue';

export type InterfaceMode = 'reader' | 'author';

export const useInterfaceMode = () => {
    const appUser = useAppUser();
    const cookieDomain = getCookieDomain();

    const modeCookie = useCookie<InterfaceMode>('interface-mode', {
        maxAge: 60 * 60 * 24 * 30, // 30 days
        sameSite: 'lax',
        domain: cookieDomain,
    });

    // Use useState for reactive state across the app, initialized from cookie or default
    const currentMode = useState<InterfaceMode>('current-interface-mode', () => {
        if (modeCookie.value) {
            return modeCookie.value;
        }
        // Default logic: author if has the role, otherwise reader
        if (appUser.value && userHasRole(appUser.value, 'author')) {
            return 'author';
        }
        return 'reader';
    });

    // Watch for mode changes to update the cookie
    watch(currentMode, (newMode) => {
        modeCookie.value = newMode;
    });

    // Sync mode if user roles change (e.g. login/logout)
    watch(appUser, (newUser) => {
        if (!newUser) return;

        const hasAuthor = userHasRole(newUser, 'author');
        const hasReader = userHasRole(newUser, 'reader');

        if (currentMode.value === 'author' && !hasAuthor) {
            currentMode.value = 'reader';
        } else if (currentMode.value === 'reader' && !hasReader && hasAuthor) {
            currentMode.value = 'author';
        }
    });

    const setMode = (mode: InterfaceMode) => {
        currentMode.value = mode;
    };

    const toggleMode = async () => {
        if (appUser.value && userHasRole(appUser.value, 'author') && userHasRole(appUser.value, 'reader')) {
            currentMode.value = currentMode.value === 'author' ? 'reader' : 'author';
            await navigateTo('/dashboard');
        }
    };

    const hasBothRoles = computed(() => {
        return appUser.value && userHasRole(appUser.value, 'author') && userHasRole(appUser.value, 'reader');
    });

    return {
        currentMode,
        setMode,
        toggleMode,
        hasBothRoles
    };
};
